import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, tap, throwError, of } from 'rxjs';
import {
  QuotasApiResponse,
  QuotaByIdResponse,
  ApplicationSubmitPayload,
  ApplicationSubmitResponse,
  SupplierProfile,
  SuppliersAllResponse
} from '../models/quota.interface';
import { ApplicationsResponse, QuotasQueryParams } from '../models/application.interface';
import { QuotaPreviewResponse, StageKey } from '../models/quota-preview.interface';
import { AssignmentsResponse } from '../models/assignment.interface';
import { ConclusionVolumesResponse } from '../models/conclusion-volume.interface';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class QuotaService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  private publicCache: QuotasApiResponse | null = null;

  private handleError(operation: string) {
    return (err: any) => {
      console.error(`Ошибка ${operation}:`, err);
      return throwError(() => err);
    };
  }


  getPublicQuotas(): Observable<QuotasApiResponse> {
    if (this.publicCache) {
      return of(this.publicCache);
    }

    return this.http
      .post<QuotasApiResponse>(
        `${this.apiUrl}/tariff-quota/api/quotas/get-all-public`,
        { page: 1, size: 15 }
      )
      .pipe(
        tap(res => (this.publicCache = res)),
        catchError(this.handleError('getPublicQuotas'))
      );
  }


  getAllQuotas(params: QuotasQueryParams): Observable<QuotasApiResponse> {
    return this.http
      .post<QuotasApiResponse>(
        `${this.apiUrl}/tariff-quota/api/quotas/get-all`,
        params
      )
      .pipe(catchError(this.handleError('getAllQuotas')));
  }


  getMyApplications(page: number = 1, size: number = 15): Observable<ApplicationsResponse> {
    return this.http
      .post<ApplicationsResponse>(
        `${this.apiUrl}/tariff-quota/api/applications/my`,
        { page, size }
      )
      .pipe(catchError(this.handleError('getMyApplications')));
  }


  getQuotaById(id: number): Observable<QuotaByIdResponse> {
    return this.http
      .get<QuotaByIdResponse>(`${this.apiUrl}/tariff-quota/api/quotas/${id}`)
      .pipe(catchError(this.handleError('getQuotaById')));
  }

  getSuppliersAll(): Observable<SuppliersAllResponse> {
    return this.http
      .post<SuppliersAllResponse>(`${this.apiUrl}/tariff-quota/api/suppliers/get-all`, { page: 1, size: 15, deleted: false })
      .pipe(catchError(this.handleError('getSuppliersAll')));
  }

  getSupplierProfile(): Observable<{ success: boolean; res: SupplierProfile }> {
    return this.http
      .get<{ success: boolean; res: SupplierProfile }>(`${this.apiUrl}/tariff-quota/api/user/external`)
      .pipe(catchError(this.handleError('getSupplierProfile')));
  }

  submitApplication(payload: ApplicationSubmitPayload): Observable<ApplicationSubmitResponse> {
    return this.http
      .post<ApplicationSubmitResponse>(
        `${this.apiUrl}/tariff-quota/api/applications/submit`,
        payload
      )
      .pipe(catchError(this.handleError('submitApplication')));
  }


  getAllApplications(params: { page: number; size: number; status?: string; quotaId?: number; deleted?: boolean }): Observable<ApplicationsResponse> {
    return this.http
      .post<ApplicationsResponse>(
        `${this.apiUrl}/tariff-quota/api/applications/get-all`,
        params
      )
      .pipe(catchError(this.handleError('getAllApplications')));
  }


  getLegalStatuses(): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/dictionary-service-ws/legal-status/get-all`, {
        type: 'LEGAL_STATUS'
      })
      .pipe(catchError(this.handleError('getLegalStatuses')));
  }


  getOrganizationByInn(inn: string): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/tariff-quota/api/minjust/organization/${inn}`)
      .pipe(catchError(this.handleError('getOrganizationByInn')));
  }


  registerSupplier(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/suppliers/register`, data)
      .pipe(catchError(this.handleError('registerSupplier')));
  }


  updateVeterinaryDocumentStatus(data: {
    applicationId: number;
    organizationInn: string;
    personIdnp: string;
    reason: string;
    token: string;
    veterinaryDocumentStatus: string;
  }): Observable<any> {
    return this.http
      .patch<any>(`${this.apiUrl}/tariff-quota/api/applications/update-veterinary-document-status`, data)
      .pipe(catchError(this.handleError('updateVeterinaryDocumentStatus')));
  }


  moderateApplication(data: {
    action: 'APPROVE' | 'REJECT';
    applicationId: number;
    organizationInn: string;
    personIdnp: string;
    token: string;
    rejectionReason?: string;
  }): Observable<any> {
    return this.http
      .patch<any>(`${this.apiUrl}/tariff-quota/api/applications/moderate`, data)
      .pipe(catchError(this.handleError('moderateApplication')));
  }


  createHistoricalVolumes(data: {
    comment: string;
    meatType: { id: number; nameRu: string; nameKy: string; alias: string };
    quotaYear: number;
    supplierId: string;
    totalImportVolume: number;
    volumeFirstYear: number;
    volumeNineMonthsPrior: number;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/historical-volumes`, data)
      .pipe(catchError(this.handleError('createHistoricalVolumes')));
  }


  setUnderConsideration(data: {
    applicationId: number;
    underConsiderationReason: string;
  }): Observable<any> {
    return this.http
      .patch<any>(`${this.apiUrl}/tariff-quota/api/applications/consideration`, data)
      .pipe(catchError(this.handleError('setUnderConsideration')));
  }


  exportApplicationPdf(applicationId: number): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/tariff-quota/api/applications/${applicationId}/export-pdf`, {
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError('exportApplicationPdf')));
  }


  updateQuotaStatus(data: {
    adminComment: string;
    organizationInn: string;
    personIdnp: string;
    quotaId: number;
    status: string;
    token: string;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/tariff-quota/api/quotas/status`, data)
      .pipe(catchError(this.handleError('updateQuotaStatus')));
  }


  createQuotasBatch(data: {
    year: number;
    limits: Array<{
      meatType: { id: number; nameRu: string; nameKy: string; alias: string };
      totalLimitVolume: number;
    }>;
    token: string;
    personIdnp: string;
    organizationInn: string;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quotas/batch`, data)
      .pipe(catchError(this.handleError('createQuotasBatch')));
  }


  getMeatTypes(): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/dictionary-service-ws/meat-type/get-all`, {})
      .pipe(catchError(this.handleError('getMeatTypes')));
  }


  distributeStage1(data: { organizationInn: string; personIdnp: string; token: string }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quota-distribution/stage-1`, data)
      .pipe(catchError(this.handleError('distributeStage1')));
  }


  distributeStage2(data: { organizationInn: string; personIdnp: string; token: string }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quota-distribution/stage-2`, data)
      .pipe(catchError(this.handleError('distributeStage2')));
  }


  distributeNewSuppliers(data: { organizationInn: string; personIdnp: string; token: string }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quota-distribution/new-supplier/distribution`, data)
      .pipe(catchError(this.handleError('distributeNewSuppliers')));
  }


  distributeStage3(data: { organizationInn: string; personIdnp: string; token: string }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quota-distribution/stage-3`, data)
      .pipe(catchError(this.handleError('distributeStage3')));
  }


  changeQuotaStatusToResidual(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/tariff-quota/api/quotas/status/residual-active`)
      .pipe(catchError(this.handleError('changeQuotaStatusToResidual')));
  }


  getQuotaPreview(quotaId: number, stage: StageKey): Observable<QuotaPreviewResponse> {
    return this.http
      .get<QuotaPreviewResponse>(`${this.apiUrl}/tariff-quota/api/quota-distribution/${quotaId}/preview/${stage}`)
      .pipe(catchError(this.handleError('getQuotaPreview')));
  }


  downloadDistributionPreviewPdf(quotaId: number, stage: StageKey): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/tariff-quota/api/quotas/distribution-preview-report-pdf?quotaId=${quotaId}&quotaStage=${stage}`, {
        responseType: 'blob'
      })
      .pipe(catchError(this.handleError('downloadDistributionPreviewPdf')));
  }


  getAllAssignments(params: { page: number; size: number }): Observable<AssignmentsResponse> {
    return this.http
      .post<AssignmentsResponse>(
        `${this.apiUrl}/tariff-quota/api/quotas/assignment/get-all`,
        params
      )
      .pipe(catchError(this.handleError('getAllAssignments')));
  }


  addVolumeFulfillment(data: {
    deliveredVolume: number;
    organizationInn: string;
    personIdnp: string;
    quotaAssignmentId: number;
    token: string;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/quota/assignment/fulfillment`, data)
      .pipe(catchError(this.handleError('addVolumeFulfillment')));
  }


  revokeAssignment(data: {
    assignmentId: number;
    reason: string;
    organizationInn: string;
    personIdnp: string;
    token: string;
  }): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/tariff-quota/api/quotas/assignment/${data.assignmentId}/revoke?reason=${encodeURIComponent(data.reason)}`,
        {
          organizationInn: data.organizationInn,
          personIdnp: data.personIdnp,
          token: data.token
        }
      )
      .pipe(catchError(this.handleError('revokeAssignment')));
  }


  getConclusionVolumes(params: { page: number; size: number }): Observable<ConclusionVolumesResponse> {
    return this.http
      .post<ConclusionVolumesResponse>(
        `${this.apiUrl}/tariff-quota/api/conclusion-volumes/get-all`,
        params
      )
      .pipe(catchError(this.handleError('getConclusionVolumes')));
  }


  createConclusionVolume(data: {
    applicantName: string;
    deliveredVolume: number;
    distributedVolume: number;
    id: null;
    meatType: { id: number; nameRu: string; nameKy: string; alias: string };
    organizationInn: string;
    personIdnp: string;
    quotaYear: number;
    remainingVolume: null;
    status: string;
    token: string;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/conclusion-volumes`, data)
      .pipe(catchError(this.handleError('createConclusionVolume')));
  }


  updateDeliveredVolume(data: { id: number; deliveredVolume: number; personIdnp: string; organizationInn: string }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/conclusion-volumes/delivered`, data)
      .pipe(catchError(this.handleError('updateDeliveredVolume')));
  }


  updateConclusionVolume(data: {
    id: number;
    applicantName: string;
    deliveredVolume: number;
    distributedVolume: number;
    meatType: { id: number; nameRu: string; nameKy: string; alias: string };
    organizationInn: string;
    personIdnp: string;
    quotaYear: number;
    status: string;
    token: string;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/tariff-quota/api/conclusion-volumes`, data)
      .pipe(catchError(this.handleError('updateConclusionVolume')));
  }
}
